export interface Categorization {
  id: number;
  transaction_id: number;
  account_name: string;
  /** Confidence data is internal-only; not sent by the API. */
  confidence_score?: number | null;
  /** Confidence data is internal-only; not sent by the API. */
  confidence_tier?: string;
  status: string;
  source: string;
  reasoning: string | null;
  created_at: string;
  description: string;
  amount: string | null;
  date: string | null;
  /**
   * Review flags (D1 amendment C48), from one server call per list request.
   * The list route always sends them; each defaults to the fail-closed
   * answer, so an item the server could not judge offers no Undo or Split.
   */
  can_undo: boolean;
  can_redo: boolean;
  can_split: boolean;
  in_books: boolean;
  can_recategorize: boolean;
  booked_by: "human" | "system" | "operator" | null;
  needs_attention: boolean;
  /** The newest audit-log line; an undo sends it back as expected_last_log_id. */
  last_log_id: number | null;
  /** ISO 8601: when the current booking took effect; null while pending. */
  confirmed_at: string | null;
  /** The transfer or refund question's detail; null (or absent) on an ordinary row. */
  fence?: FenceDetail | null;
}

/** Why a row is held for the client's answer (the API's `FenceDetail`). */
export interface FenceDetail {
  verdict: "likely_transfer" | "held_counterpart" | "positive_liability" | string;
  /** Transaction ids of the business-side rows this one may be a transfer with. */
  business_matches: number[];
  /** Transaction ids of matching rows on an account the client marked personal. */
  held_matches: number[];
  note: string | null;
}

/** A banner above the review list (the API's `AccountBanner`); `message` is the server's. */
export interface AccountBanner {
  kind: string;
  last6: string;
  nickname: string | null;
  opening_as_of: string | null;
  transaction_count: number;
  message: string;
}

export interface CategorizationListResponse {
  items: Categorization[];
  total: number;
  /** False when the books could not be read: no item then offers Undo. */
  ledger_readable: boolean;
  /** The held-account and before-opening banners; absent from older responses. */
  banners?: AccountBanner[];
}

/** The pick list (GET /accounts): the chart's names and the server's label for some. */
export interface AccountChoices {
  accounts: string[];
  labels: Record<string, string>;
}

/** One row the review answer re-scored, restored or left (the API's `RescoredRow`). */
export interface RescoredRow {
  transaction_id: number;
  outcome: "rescored" | "restored" | "unchanged";
}

/** A review PATCH's answer (the API's `UpdateCategorizationResponse`). */
export interface ReviewAnswer {
  status: string;
  new_account?: string | null;
  last_log_id?: number | null;
  can_undo?: boolean | null;
  can_redo?: boolean | null;
  adjusts_period?: string | null;
  rescored?: RescoredRow[] | null;
}

/** An account the upload saw for the first time (the API's `NewAccount`). */
export interface NewAccount {
  account_row_id: number;
  last6: string;
  institution: string | null;
  account_type: string | null;
  pending_questions: string[];
  predicted_opening: string | null;
  predicted_as_of: string | null;
}

/** The categorization status values that mean the job is still running. */
export const RUNNING_JOB_STATUSES: readonly string[] = ["queued", "in_progress"];

/** The import job's status (the API's `ImportJobStatus`, unwrapped from `data`). */
export interface ImportJobStatus {
  job_id: string;
  import: { status: string; imported: number; skipped: number; errored: number };
  categorization: { status: string; total: number; processed: number };
}

/** One row the import could not read (the API's `ImportErrorDetail`). */
export interface ImportErrorDetail {
  /** The row's place in its statement, counted from 0. */
  index: number;
  fitid?: string | null;
  /** A fixed sentence chosen by the row's shape, never the exception's text. */
  error: string;
}

/** What one upload did with statements that named no account. */
export interface UnnamedAccounts {
  /** The server's sentence when the file had such a statement, else null. */
  notice: string | null;
  possible_second_account: number;
  deletes_without_match: number;
  deletes_with_several: number;
}

/** The upload's answer (the API's `ImportResponse`, POST /bank/upload). */
export interface ImportResult {
  imported: number;
  skipped: number;
  errored: number;
  summary: string;
  errors: ImportErrorDetail[];
  notes: string[];
  unnamed_accounts: UnnamedAccounts;
  categorization_status: string | null;
  job_id: string | null;
  /** Accounts seen for the first time; absent from older responses. */
  new_accounts?: NewAccount[];
  statement_date_start: string | null;
  statement_date_end: string | null;
  /** Zero at response time by the API's contract (PS-58); kept for compatibility. */
  auto_categorized: number;
  /** Zero at response time by the API's contract (PS-58); kept for compatibility. */
  pending_review: number;
  /** Zero at response time by the API's contract (PS-58); kept for compatibility. */
  categorization_failed: number;
}
